import { CallTransfer } from 'types';

/**
 * The API sends snake_case; this file's `CallTransfer` type is camelCase.
 *
 * Nothing translated between them, so EVERY field read on the Transfers page
 * (`transfer.callerId`, `transfer.transferredFrom`, …) was `undefined` against
 * real rows — and `transfer.transferType.toUpperCase()` threw, white-screening
 * the page as soon as any data actually arrived. TypeScript could not catch it:
 * the response is cast to `CallTransfer`, which declares every field present and
 * non-optional, so the compiler believed a shape the server never sends.
 *
 * Verified against production: 34 rows from "Tata Mobile (tunnel)" carrying
 * `caller_id`, `transfer_type`, `transferred_from`, …
 *
 * ⚠ The switch also leaves fields genuinely NULL — on all 34 rows,
 * `transfer_type`, `transfer_status`, `transferred_to`, `new_call_id` and
 * `transfer_call_summary` are null. So a rename alone would still have thrown;
 * the nulls have to survive as nulls and the render has to tolerate them. That
 * is a real gap in what the switch records, not something to paper over with a
 * default that invents a transfer type nobody measured.
 */
export function normalizeTransfer(raw: any): CallTransfer {
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = raw?.[k];
      if (v !== undefined && v !== null && v !== '') return v;
    }
    return null;
  };
  return {
    // The federated rows carry no surrogate id — they are keyed by call id — so
    // the original call id doubles as the React key rather than `undefined`.
    id: pick('id') ?? pick('original_call_id', 'originalCallId'),
    businessId: pick('business_id', 'businessId'),
    originalCallId: pick('original_call_id', 'originalCallId'),
    newCallId: pick('new_call_id', 'newCallId'),
    callerId: pick('caller_id', 'callerId'),
    transferredTo: pick('transferred_to', 'transferredTo'),
    transferredFrom: pick('transferred_from', 'transferredFrom'),
    transferType: pick('transfer_type', 'transferType'),
    transferCallSummary: pick('transfer_call_summary', 'transferCallSummary'),
    transferStatus: pick('transfer_status', 'transferStatus'),
    transferTime: pick('transfer_time', 'transferTime'),
    completedTime: pick('completed_time', 'completedTime'),
    createdAt: pick('created_at', 'createdAt'),
    source: pick('_source', 'source'),
  } as CallTransfer;
}
